import type { IPageBasedPagination } from '../../types/content_switchers/pagination';
import type {
  IDataTable,
  IPaginationBar,
  TDataTablePagination,
} from '../../types/data_table';

import { getValueFromNestedObject } from '../../utils/getValueFromNestedObject';

/**
 * Assembles the pager's props, and decides whether there should be a pager at
 * all. Kept out of `DataTable` because half of it is bookkeeping — reading
 * totals and cursors back out of the response by path — and the other half is
 * a visibility rule with a history worth writing down.
 */

export interface IBuildPaginationProps {
  /** The consumer's `pagination` config, or `undefined` for no pager. */
  pagination?: TDataTablePagination;
  lang: IDataTable['lang'];
  /** The raw response, which carries the totals and cursors. */
  response: any;
  /** The rows, used for the "showing n of m" count. */
  rows: unknown;
  currentPage: number;
  pageSize: number;
  /** Whether the table itself is fetching. */
  isTableLoading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

/**
 * The half of the props that depends on how the API paginates: a total item
 * count for page-based APIs, cursors for cursor-based ones.
 */
const buildPaginationModeProps = (
  pagination: TDataTablePagination,
  response: any
) => {
  // `dataLocation` is required by the types but can genuinely be missing at
  // runtime — a consumer that pages without reading totals back out.
  const { type, dataLocation } = pagination as {
    type?: string;
    dataLocation?: Partial<{ totalItems: string; next: string; prev: string }>;
  };

  if (type === 'page_based') {
    const { maxVisiblePages = 3, compactMode } =
      pagination as IPageBasedPagination;

    return {
      type,
      totalItems: getValueFromNestedObject(
        response,
        dataLocation?.totalItems || ''
      ),
      maxVisiblePages,
      compactMode,
    } as const;
  }

  if (type === 'cursor_based') {
    return {
      type,
      next: getValueFromNestedObject(response, dataLocation?.next || ''),
      prev: getValueFromNestedObject(response, dataLocation?.prev || ''),
    } as const;
  }

  return undefined;
};

/**
 * Returns the props for `PaginationBar`, or `undefined` when no pager should
 * render.
 *
 * The pager used to be gated purely on there being rows. react-query drops
 * `data` whenever the key changes, so every filter change and page step
 * unmounted the whole bar and the table jumped. Keeping it mounted in its
 * loading state holds the space instead — and it is the only way
 * `PaginationBar`'s skeleton branch is ever reached.
 */
export const buildPaginationProps = ({
  pagination,
  lang,
  response,
  rows,
  currentPage,
  pageSize,
  isTableLoading,
  onPageChange,
  onPageSizeChange,
}: IBuildPaginationProps): IPaginationBar | undefined => {
  if (!pagination || !Object.keys(pagination).length) return undefined;

  const isLoading = !!pagination.isLoading || isTableLoading;
  const hasRows = Array.isArray(rows) && rows.length > 0;

  if (!isLoading && !hasRows) return undefined;

  return {
    ...buildPaginationModeProps(pagination, response),
    lang,
    itemsPerPage: Array.isArray(rows) ? rows.length : undefined,
    currentPageSize: pageSize,
    pageSizeOptions: pagination.pageSizeOptions,
    defaultSelectedPage: currentPage,
    localization: pagination.localization,
    hideLabels: pagination.hideLabels,
    isMobile: pagination.isMobile,
    canChangePageSize: pagination.canChangePageSize,
    isLoading,
    onPageSizeChange,
    onPageChange,
    // Wrapped rather than passed straight through: the pager treats a missing
    // handler as "this control does nothing", which is not what an omitted
    // consumer callback means.
    onNextHandler: (pageNum: number) => pagination.onNextHandler?.(pageNum),
    onPreviousHandler: (pageNum: number) =>
      pagination.onPreviousHandler?.(pageNum),
    onPageClickHandler: (pageNum: number) =>
      pagination.onPageClickHandler?.(pageNum),
  } as IPaginationBar;
};
